'use client';

import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

import PatternBackground from 'components/ui/PatternBackground';
import emotionalBg from '../../images/marketing/emotional-bg.webp';

export default function EmotionalSection() {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const rawY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y1 = useSpring(rawY, { stiffness: 90, damping: 25, restDelta: 0.001 });
  const opacity1 = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.3, 1, 1, 0.3]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[50vh] sm:min-h-[60vh] md:min-h-[80vh] flex flex-col items-center justify-center bg-Mora-dark text-Mora-cream overflow-hidden py-14 sm:py-18 md:py-24"
    >
      <div className="absolute inset-0 z-0">
        <img src={emotionalBg} alt="Sharing peanuts" className="w-full h-full object-cover opacity-50" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-Mora-dark via-Mora-dark/70 to-black/40 z-10 pointer-events-none" />
      <PatternBackground className="opacity-10 z-10 pointer-events-none" />

      <motion.div
        style={{ y: y1, opacity: opacity1 }}
        className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-20 text-center max-w-3xl flex flex-col items-center"
      >
        <div className="w-9 sm:w-12 h-9 sm:h-12 mb-4 sm:mb-8 opacity-80" style={{ color: 'var(--Mora-gold, #c19a5b)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
          </svg>
        </div>

        <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-heading text-Mora-cream mb-4 sm:mb-8 leading-tight">
          Every Bite Should Feel <br className="hidden md:block" />
          <span className="text-Mora-caramel italic">Like Home.</span>
        </h2>

        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-Mora-beige font-body leading-relaxed max-w-2xl mb-6 sm:mb-12 px-2 sm:px-0">
          Close your eyes. Remember the scent of roasted peanuts filling the air in your grandparents' home? The shared bowls during family gatherings, the laughter, the warmth. Mora isn't just about taste. It's about recreating that feeling of belonging, beautifully packaged for the modern connoisseur.
        </p>

        <div className="w-20 sm:w-24 h-px bg-gradient-to-r from-transparent via-Mora-gold to-transparent opacity-50" />
      </motion.div>
    </section>
  );
}
